/**
 * GoalsPanel — collapsible at-a-glance goal status surface mounted on the
 * /exec/dashboard above the widget grid (Phase B10/D5). Lists active goals
 * with their target values and inline edit/delete affordances. Hosts the
 * compact `GoalEditor` modal.
 *
 * Role (Phase B10): dashboard sidebar surface. Pair with `GoalEditor.tsx`
 * (compact inline editor used here) and `GoalEditorFull.tsx` (canonical
 * full-page modal used on /exec/goals).
 */
import { useState } from 'react'
import { Target, Plus, Pencil, Trash2, ChevronDown, ChevronRight } from 'lucide-react'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'
import { execAPI } from '../../services/exec'
import GoalEditor from './GoalEditor'
import type { Goal } from '../../types/exec'

interface Props {
  officeId: number | null
}

export default function GoalsPanel({ officeId }: Props) {
  const { goals, measures, refetchGoals } = useExecDashboard()
  const [expanded, setExpanded] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null)

  const handleEdit = (goal: Goal) => {
    setEditingGoal(goal)
    setEditorOpen(true)
  }

  const handleDelete = async (goalId: string) => {
    try {
      await execAPI.deleteGoal(goalId)
      await refetchGoals()
    } catch (err) {
      console.error('Failed to delete goal:', err)
    }
  }

  const handleNewGoal = () => {
    setEditingGoal(null)
    setEditorOpen(true)
  }

  const formatTarget = (goal: Goal) => {
    const { value, direction, unit } = goal.target_value
    const prefix = direction === 'lower_is_better' ? '≤' : '≥'
    const suffix = unit === 'days' ? ' days' : unit === 'percent' ? '%' : unit === 'dollars' ? '$' : ''
    if (unit === 'dollars') return `${prefix} $${value.toLocaleString()}`
    return `${prefix} ${value}${suffix}`
  }

  return (
    <>
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="w-full flex items-center justify-between px-4 py-3 text-left"
        >
          <div className="flex items-center gap-2">
            <Target size={16} className="text-blue-500" />
            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Goals
            </span>
            {goals.length > 0 && (
              <span className="text-xs text-gray-400 bg-gray-100 dark:bg-gray-800 rounded-full px-1.5 py-0.5">
                {goals.length}
              </span>
            )}
          </div>
          {expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />}
        </button>

        {/* Body */}
        {expanded && (
          <div className="px-4 pb-3 space-y-2">
            {goals.length === 0 ? (
              <p className="text-xs text-gray-400 py-2">
                No goals set. Goals shape acquisition workforce behavior across the portfolio.
              </p>
            ) : (
              goals.map((goal) => {
                const measureDef = measures[goal.measure]
                return (
                  <div
                    key={goal.id}
                    className="flex items-start justify-between py-2 border-t border-gray-100 dark:border-gray-800 first:border-t-0"
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {measureDef?.label ?? goal.measure}
                      </div>
                      <div className="text-xs text-blue-600 dark:text-blue-400 font-mono mt-0.5">
                        {formatTarget(goal)}
                      </div>
                      {goal.description && (
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          {goal.description}
                        </div>
                      )}
                      {goal.office_name && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {goal.office_name}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5 ml-2 shrink-0">
                      <button
                        onClick={() => handleEdit(goal)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600"
                        title="Edit goal"
                      >
                        <Pencil size={12} />
                      </button>
                      <button
                        onClick={() => handleDelete(goal.id)}
                        className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-400 hover:text-red-500"
                        title="Remove goal"
                      >
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                )
              })
            )}

            {/* Add goal button */}
            <button
              onClick={handleNewGoal}
              className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium mt-1"
            >
              <Plus size={14} />
              Set a goal
            </button>
          </div>
        )}
      </div>

      {/* Goal Editor Modal */}
      <GoalEditor
        open={editorOpen}
        onClose={() => {
          setEditorOpen(false)
          setEditingGoal(null)
        }}
        officeId={officeId}
        existingGoal={editingGoal}
      />
    </>
  )
}
